import { FC, useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router'
import { Breadcrumbs, Button, Input } from '@mondra/ui-components'
import { useQuery } from '@apollo/client'
import { ISale } from './types'
import { getSalesVars } from './helpers'
import { filterEnums, getTableData } from '../../../shared/gridFunctions'
import Table from '../../../components/Table'
import { GET_ALL_SALES } from '../../../query/sale-activity'
import { EXPORT_SALES_QUERY } from '../../../query/export-sales'
import { ExportGraphqlButton } from '../../../components/ExportGraphqlButton'
import { EXPORT_MAX_ROW_COUNT } from '../../../const'

const SalesActivity: FC = () => {
  const navigate = useNavigate()

  const [tableData, setTableData] = useState<ISale[]>([])
  const [columnDefs, setColumn] = useState([])
  const [tableSearch, setTableSearch] = useState<string | null>(null)
  const [debouncedTableSearch, setDebouncedTableSearch] = useState(tableSearch)
  const [totalCount, setTotalCount] = useState<string>('')
  const [endCursor, setEndCursor] = useState<string | null>(null)
  const [startCursor, setStartCursor] = useState<string | null>(null)
  const [salesVars, setSalesVars] = useState<any>(getSalesVars)
  const [order, setOrder] = useState([{ id: 'ASC' }])

  const { data, loading, error, refetch } = useQuery(GET_ALL_SALES, {
    variables: {
      str: '',
      first: 100,
      last: null,
      order: order,
      ...getSalesVars,
    },
  })

  const anyRefetch = (variables?: any): Promise<any> => {
    return refetch(variables)
  }

  const setCursors = () => {
    setEndCursor(data.salesActivities.pageInfo.endCursor)
    setStartCursor(data.salesActivities.pageInfo.startCursor)
  }

  useEffect(() => {
    const timer = setTimeout(() => setTableSearch(debouncedTableSearch), 1000)
    return () => clearTimeout(timer)
  }, [debouncedTableSearch])

  useEffect(() => {
    refetch()
    getTableData(
      data,
      loading,
      setTableData,
      setColumn,
      setTotalCount,
      setCursors,
      '/food/sale-activity/sale',
      'salesActivities',
      'salesActivity'
    )
  }, [data])

  useEffect(() => {
    if (tableSearch != null || !order[0].id) {
      if (!loading) {
        setSalesVars({ ...filterEnums(tableSearch, getSalesVars, data) })
        anyRefetch({
          str: tableSearch || '',
          ...filterEnums(tableSearch, getSalesVars, data),
          first: 100,
          after: null,
          before: null,
          last: null,
          order: order,
        })
      }
    }
  }, [tableSearch, order, loading])

  const exportPayload = useMemo(
    () => ({
      operationName: 'SalesActivities',
      query: EXPORT_SALES_QUERY.loc?.source.body,
      variables: {
        first: EXPORT_MAX_ROW_COUNT,
        after: null,
        where: null,
        order,
      },
    }),
    [order]
  )

  return (
    <div className={'packaging'}>
      <div style={{ maxWidth: '50rem' }} className={'mb-4'}>
        <p className={'mt-3 decoration-gray-600'}>
          <Breadcrumbs routes={[{ path: '/food/sale-activity', title: 'Sales Activity Table' }]} />
        </p>
        <h2 className={'mb-5 mt-3 text-2xl'}>Sales Activity</h2>
        <div className="packaging-actions items-center">
          <Input
            onChange={e => setDebouncedTableSearch(e.target.value)}
            iconType="search"
            placeholder="Search"
          />
          <Button
            onClick={() => navigate('/food/sale-activity/sale?itemId=new')}
            className={'bg-brand-enabled'}
          >
            Add Sale
          </Button>
          <ExportGraphqlButton payload={exportPayload} />
        </div>
      </div>
      <Table
        loading={loading}
        tableData={tableData}
        columnDefs={columnDefs}
        totalNumber={totalCount}
        endCursor={endCursor}
        startCursor={startCursor}
        refetch={refetch}
        defaultVars={salesVars || getSalesVars}
        setCursors={setCursors}
        tableSearch={tableSearch}
        setOrder={setOrder}
        order={order}
        suppressRowClickSelection
      />
    </div>
  )
}

export default SalesActivity
